/**
 * 카메라 아이콘 SVG를 동적으로 생성하여 HTMLImageElement로 변환
 * Figma 디자인 기준: 동일한 카메라 형태에 색상만 다름
 *   - Default: #696F8C
 *   - Selected/Focused: #484BD2
 *   - Edit(조정): #EB6227
 *   - Add(추가): #EB2F96
 *
 * 아래 색은 DS 토큰이 아니라 raw hex다. mash-design-system 이 이름으로 주는 스케일은
 * GR(회색)·MB(메이아이 블루) 뿐이고 주황·마젠타 스케일이 없다. #696F8C 만 GR700 과 같다.
 */

const CAMERA_SVG_TEMPLATE = (color: string) => `
<svg width="84" height="84" viewBox="0 0 71 57" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M60.7423 13.4864C55.5881 11.2793 49.9918 10.2991 44.3943 10.6231C38.7968 10.947 33.3509 12.5663 28.4858 15.3533C23.6206 18.1403 19.469 22.0189 16.3581 26.6836C13.2471 31.3483 11.2618 36.6716 10.5585 42.2342L46.4868 46.7769L60.7423 13.4864Z" fill="${color}" fill-opacity="0.2"/>
<g filter="url(#f1)">
<ellipse cx="46.487" cy="46.7769" rx="4.46012" ry="4.46012" transform="rotate(118.118 46.487 46.7769)" fill="${color}"/>
<path d="M50.8644 49.1158C49.5727 51.5333 46.5658 52.4458 44.1482 51.1541C41.7307 49.8623 40.8181 46.8555 42.1099 44.4379C43.4018 42.0205 46.4086 41.1079 48.8261 42.3996C51.2436 43.6914 52.1561 46.6983 50.8644 49.1158Z" stroke="white" stroke-width="2"/>
</g>
<path d="M60.7424 13.4863C55.5882 11.2792 49.9919 10.299 44.3944 10.623C38.7968 10.947 33.351 12.5663 28.4858 15.3533C23.6207 18.1403 19.4691 22.0189 16.3582 26.6835C13.2472 31.3482 11.2619 36.6715 10.5586 42.2341" stroke="${color}" stroke-width="2"/>
<defs>
<filter id="f1" x="38" y="38.3" width="17" height="17" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
<feFlood flood-opacity="0" result="bg"/><feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="a"/><feOffset/><feGaussianBlur stdDeviation="1.5"/><feColorMatrix type="matrix" values="0 0 0 0 0.05 0 0 0 0 0.09 0 0 0 0 0.29 0 0 0 0.25 0"/><feBlend in2="bg" result="s"/><feBlend in="SourceGraphic" in2="s" result="shape"/>
</filter>
</defs>
</svg>`;

/**
 * 화각 조정 "조정 전(ghost)" 전용 — 부채꼴 외곽선만(속 빔, dot 없음).
 * 조정 후(솔리드)와 같은 자리에 겹쳐도 테두리 호가 위로 보이고, unselected 회색과도 섞이지 않도록 앰버 사용.
 */
const CAMERA_GHOST_SVG_TEMPLATE = (color: string) => `
<svg width="84" height="84" viewBox="0 0 71 57" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M60.7423 13.4864C55.5881 11.2793 49.9918 10.2991 44.3943 10.6231C38.7968 10.947 33.3509 12.5663 28.4858 15.3533C23.6206 18.1403 19.469 22.0189 16.3581 26.6836C13.2471 31.3483 11.2618 36.6716 10.5585 42.2342L46.4868 46.7769L60.7423 13.4864Z" fill="none" stroke="${color}" stroke-width="2.5"/>
</svg>`;

function svgToDataUri(svg: string): string {
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

function createIcon(color: string): HTMLImageElement {
  const img = new Image();
  img.src = svgToDataUri(CAMERA_SVG_TEMPLATE(color));
  return img;
}

// 디자인 시스템 색상
export const COLOR_DEFAULT = "#696F8C";
export const COLOR_FOCUSED = "#484BD2";
export const COLOR_EDIT = "#EB6227";
// 추가는 조정과 다른 계열이어야 함 — 기존(회색남색)·선택(파랑)·조정(주황)과 겹치지 않는
// 유일한 계열이고, 적록 색각 이상에서도 주황과 갈라지는 마젠타 사용
export const COLOR_ADD = "#EB2F96";
// 조정 전(ghost) 전용 — 조정 후 주황(#EB6227)에서 살짝 노란톤으로 뺀 앰버
export const COLOR_GHOST = "#F0851C";

export const CAMERA_ICON_DEFAULT = createIcon(COLOR_DEFAULT);
export const CAMERA_ICON_FOCUSED = createIcon(COLOR_FOCUSED);
export const CAMERA_ICON_EDIT = createIcon(COLOR_EDIT);
export const CAMERA_ICON_ADD = createIcon(COLOR_ADD);

// 화각 조정 전(ghost) — 앰버 부채꼴 외곽선
export const CAMERA_ICON_GHOST = (() => {
  const img = new Image();
  img.src = svgToDataUri(CAMERA_GHOST_SVG_TEMPLATE(COLOR_GHOST));
  return img;
})();
